import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Package, Truck, CheckCircle2, XCircle, Loader2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import LoginModal from '../components/LoginModal';

const statusMap = {
  'Entregado': { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  'En camino': { icon: Truck, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30' },
  'Preparando': { icon: Package, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/30' },
  'Cancelado': { icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/30' },
};

const OrdersPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openLogin, setOpenLogin] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    setLoading(true);
    api.get('/orders').then((r) => setOrders(r.data)).catch(() => setOrders([])).finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) return <div className="max-w-6xl mx-auto px-4 mt-20 text-white/60"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando...</div>;

  if (!user) return (
    <div className="max-w-2xl mx-auto px-4 mt-20 pb-32 text-center">
      <div className="w-20 h-20 mx-auto rounded-full bg-orange-500/15 text-orange-400 flex items-center justify-center">
        <Lock className="w-8 h-8" />
      </div>
      <h1 className="mt-6 text-3xl font-black text-white">Ingresa para ver tus pedidos</h1>
      <p className="mt-2 text-white/60">Necesitas iniciar sesión para consultar tu historial.</p>
      <Button onClick={() => setOpenLogin(true)} className="mt-6 bg-orange-500 hover:bg-orange-600 text-black font-bold">Iniciar sesión</Button>
      <LoginModal open={openLogin} onOpenChange={setOpenLogin} />
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-6 mt-6 pb-32">
      <h1 className="text-3xl md:text-4xl font-black text-white">Mis pedidos</h1>
      <p className="text-white/50 mt-1">Consulta el estado de tus pedidos.</p>

      {orders.length === 0 ? (
        <div className="mt-10 py-16 bg-neutral-950 border border-white/10 rounded-2xl text-center">
          <Package className="w-10 h-10 mx-auto text-white/40" />
          <p className="mt-3 font-semibold text-white/80">Aún no tienes pedidos</p>
          <Link to="/catalogo" className="mt-4 inline-block">
            <Button className="bg-orange-500 hover:bg-orange-600 text-black">Explorar catálogo</Button>
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {orders.map((o) => {
            const s = statusMap[o.status] || statusMap['Preparando'];
            const Icon = s.icon;
            return (
              <div key={o.order_id} className="bg-neutral-950 rounded-2xl border border-white/10 p-5 hover:border-orange-500/30 transition">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-white/50">Pedido</p>
                    <p className="font-bold text-white">{o.order_id}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full border ${s.bg} ${s.color}`}>
                    <Icon className="w-4 h-4" /> {o.status}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-white/50">Fecha</p><p className="font-semibold text-white">{new Date(o.created_at).toLocaleDateString('es-MX')}</p></div>
                  <div><p className="text-white/50">Artículos</p><p className="font-semibold text-white">{o.items?.length || 0}</p></div>
                  <div><p className="text-white/50">Total</p><p className="font-semibold text-white">${o.total?.toLocaleString('es-MX')}</p></div>
                </div>
                {o.address && <p className="mt-3 text-xs text-white/50 truncate">📍 {o.address}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
